import React from 'react';
import api from '../services/api';
import GpoFilter from './GpoFilter';

// Dispatch & Packing (spec a35dc68, mig 154). Order-Execution tab. Packing List → Dispatch → FG.
const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
const th = { padding: '8px 10px', textAlign: 'left', color: '#555', fontWeight: 600, borderBottom: '2px solid #eee', fontSize: 11, whiteSpace: 'nowrap' };
const td = { padding: '7px 10px', fontSize: 13, borderBottom: '1px solid #f5f5f5' };
const inp = { padding: '7px 9px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
const btn = { padding: '7px 14px', background: '#3B3A8F', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 600 };
const PLC = { draft: '#888', completed: '#166534', cancelled: '#B91C1C', dispatched: '#3B3A8F' };
const pill = (s) => <span style={{ padding: '2px 9px', borderRadius: 8, fontSize: 11, fontWeight: 700, color: '#fff', background: PLC[s] || '#888' }}>{s}</span>;
const subBtn = (id, cur, set, label) => <button key={id} onClick={() => set(id)} style={{ padding: '7px 14px', border: 'none', borderBottom: cur === id ? '3px solid #3B3A8F' : '3px solid transparent', background: 'transparent', color: cur === id ? '#3B3A8F' : '#888', fontWeight: cur === id ? 700 : 500, fontSize: 13, cursor: 'pointer' }}>{label}</button>;
// ── ASN (mig 233) ────────────────────────────────────────────────────────────────────────────
// The buyer's Advance Shipping Notice: raised and approved in THEIR system, arriving here by
// email. We store the reference and the proof and generate neither. The dispatch gate reads
// BOTH fields, so "has an ASN" means both — a reference with no approval date is not an
// approval, which is also the DB CHECK (chk_packing_list_asn).
const FILE_ORIGIN = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3000';
const asnFileHref = (name) => (name ? `${FILE_ORIGIN}/api/v1/files/${name}` : null);
const hasAsn = (pl) => !!(pl && pl.asn_ref && pl.asn_approved_at);
const dstr = (d) => (d ? String(d).slice(0, 10) : '—');
const asnChip = (pl) => (hasAsn(pl)
  ? <span title={`ASN ${pl.asn_ref} · approved ${dstr(pl.asn_approved_at)}`} style={{ padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 700, color: '#166534', background: '#E1EFE7' }}>✓ ASN</span>
  : <span title="The buyer has not confirmed these goods — a dispatch will be refused." style={{ padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 700, color: '#B45309', background: '#FDF2E2' }}>no ASN</span>);

const Modal = ({ title, onClose, children, wide }) => (
  <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
    <div style={{ background: '#fff', borderRadius: 12, width: '92%', maxWidth: wide ? 780 : 480, padding: 22, maxHeight: '88vh', overflowY: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}><b style={{ fontSize: 16 }}>{title}</b><button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 20, cursor: 'pointer', color: '#888' }}>✕</button></div>
      {children}
    </div>
  </div>
);

export default function DispatchTab({ user }) {
  const [sub, setSub] = React.useState('pl');
  return (
    <div>
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #eee', marginBottom: 14 }}>
        {subBtn('pl', sub, setSub, '📦 Packing Lists')}
        {subBtn('dc', sub, setSub, '🚚 Dispatch')}
        {subBtn('fg', sub, setSub, '🏬 FG on Hand')}
      </div>
      {sub === 'pl' ? <PackingLists user={user} /> : sub === 'dc' ? <Dispatches user={user} /> : <FGView />}
    </div>
  );
}

function PackingLists({ user }) {
  const [rows, setRows] = React.useState([]); const [sel, setSel] = React.useState(null); const [creating, setCreating] = React.useState(false);
  // SERVER-SIDE: GET /dispatch/packing-lists is capped at LIMIT 500. A client-side filter
  // would search only the 500 that happened to load and would look like it worked.
  const [gpoQ, setGpoQ] = React.useState('');
  const load = React.useCallback(() => api.get('/dispatch/packing-lists' + (gpoQ.trim() ? '?gpo_no=' + encodeURIComponent(gpoQ.trim()) : ''))
    .then(r => setRows(r.data.data || [])).catch(() => {}), [gpoQ]);
  React.useEffect(() => { load(); }, [load]);
  const open = (id) => api.get('/dispatch/packing-lists/' + id).then(r => setSel(r.data.data)).catch(() => {});
  if (sel) return <PLDetail pl={sel} onBack={() => { setSel(null); load(); }} reload={() => open(sel.id)} />;
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <GpoFilter value={gpoQ} onChange={setGpoQ} />
          <span style={{ fontSize: 12, color: '#888' }}>One row per physical carton. Complete a PL to make it dispatchable.</span>
        </div>
        <button onClick={() => setCreating(true)} style={btn}>+ New Packing List</button>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr>{['PL No', 'GPO', 'Style', 'Buyer', 'Pack', 'Cartons', 'ASN', 'Status'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={8} style={{ ...td, textAlign: 'center', color: '#aaa' }}>{gpoQ.trim() ? `No packing list matches GPO “${gpoQ.trim()}”.` : 'No packing lists yet.'}</td></tr>}
          {rows.map(r => (
            <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => open(r.id)}>
              <td style={{ ...td, fontFamily: 'monospace', fontWeight: 700, color: '#3B3A8F' }}>{r.pl_no}</td>
              <td style={td}>{r.gpo_no}</td><td style={td}>{r.style_no || '—'}</td><td style={td}>{r.buyer || '—'}</td>
              <td style={{ ...td, fontSize: 11 }}>{r.pack_type}</td><td style={{ ...td, textAlign: 'center' }}>{r.carton_count}</td>
              {/* Which of these can actually ship, without opening each one. */}
              <td style={td}>{asnChip(r)}</td><td style={td}>{pill(r.status)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {creating && <NewPL onClose={() => setCreating(false)} onSaved={(id) => { setCreating(false); load(); open(id); }} />}
    </div>
  );
}

function NewPL({ onClose, onSaved }) {
  const [gpo, setGpo] = React.useState(''); const [err, setErr] = React.useState(''); const [busy, setBusy] = React.useState(false);
  const save = async () => { setErr(''); if (!gpo.trim()) return setErr('GPO no required.'); setBusy(true);
    try { const r = await api.post('/dispatch/packing-lists', { gpo_no: gpo.trim() }); onSaved(r.data.data.id); }
    catch (e) { setErr(e.response?.data?.error || 'Failed'); } finally { setBusy(false); } };
  return <Modal title="New Packing List" onClose={onClose}>
    <label style={{ fontSize: 12, color: '#555' }}>GPO no *<br /><input value={gpo} onChange={e => setGpo(e.target.value)} style={{ ...inp, width: '100%', margin: '3px 0 10px' }} placeholder="style & buyer auto-fill" /></label>
    {err && <div style={{ color: '#B91C1C', fontSize: 13, marginBottom: 8 }}>{err}</div>}
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}><button onClick={onClose} style={{ ...btn, background: '#f0f2f8', color: '#555' }}>Cancel</button><button onClick={save} disabled={busy} style={btn}>Create</button></div>
  </Modal>;
}

function PLDetail({ pl, onBack, reload }) {
  const [adding, setAdding] = React.useState(false); const [recon, setRecon] = React.useState(null);
  const [asnOpen, setAsnOpen] = React.useState(false);
  // FROZEN ONCE USED. The route locks asn_ref/asn_approved_at as soon as any dispatch exists
  // against this PL, keyed on dispatch_packing_lists. The nearest thing the detail payload
  // carries is a dispatched carton, which covers every real case — a PL is only ever linked to
  // a truck by putting cartons on it. The edge case (linked with none of its cartons aboard)
  // falls through to the route's own 409, which says exactly what happened. The server is the
  // authority here; this only decides whether to offer the button.
  const plLocked = (pl.cartons || []).some(c => c.dispatched_on);
  const act = async (verb) => { try { await api.post(`/dispatch/packing-lists/${pl.id}/${verb}`); reload(); } catch (e) { alert(e.response?.data?.error || verb + ' failed'); } };
  const delCarton = async (cid) => { try { await api.delete(`/dispatch/packing-lists/${pl.id}/cartons/${cid}`); reload(); } catch (e) { alert(e.response?.data?.error || 'delete failed'); } };
  const loadRecon = () => api.get('/dispatch/reconciliation?gpo_no=' + encodeURIComponent(pl.gpo_no)).then(r => setRecon(r.data.data)).catch(() => {});
  return (
    <div>
      <button onClick={onBack} style={{ ...btn, background: '#f0f2f8', color: '#555', marginBottom: 12 }}>← Back</button>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
        <div><div style={{ fontSize: 18, fontWeight: 800, color: '#3B3A8F' }}>{pl.pl_no} {pill(pl.status)}</div>
          <div style={{ fontSize: 12, color: '#888' }}>GPO {pl.gpo_no} · {pl.style_no || '—'} · {pl.buyer || '—'} · {pl.pack_type}</div></div>
        <div style={{ display: 'flex', gap: 8 }}>
          {pl.status === 'draft' && <button onClick={() => setAdding(true)} style={btn}>+ Carton</button>}
          {pl.status === 'draft' && <button onClick={() => act('complete')} style={{ ...btn, background: '#166534' }}>Complete</button>}
          <button onClick={loadRecon} style={{ ...btn, background: '#6d28d9' }}>Pack-vs-Cut</button>
        </div>
      </div>
      {/* THE GATE, MADE VISIBLE. mig 233 refuses a dispatch for any PL without both an ASN
          reference and the buyer's approval date. This panel is the only place either can be
          entered — before it existed the refusal at dispatch was unanswerable. */}
      <div style={{ border: '1px solid ' + (hasAsn(pl) ? '#BBE5CC' : '#F3D7A8'), background: hasAsn(pl) ? '#F3FAF6' : '#FFFBF2', borderRadius: 8, padding: '11px 14px', marginBottom: 14, display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 320px', minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#555', letterSpacing: 0.4, textTransform: 'uppercase', marginBottom: 4 }}>Buyer's ASN</div>
          {hasAsn(pl) ? (
            <>
              <div style={{ fontSize: 13 }}>
                <b style={{ fontFamily: 'monospace' }}>{pl.asn_ref}</b>
                <span style={{ color: '#555' }}> · approved by the buyer on <b>{dstr(pl.asn_approved_at)}</b></span>
              </div>
              {/* TWO DATES, NEVER MERGED: when the buyer approved, and when RSR keyed it. A gap
                  between them is a fact about how fast we record, and collapsing them hides it. */}
              <div style={{ fontSize: 11, color: '#888', marginTop: 3 }}>
                Recorded here {pl.asn_recorded_at ? 'on ' + dstr(pl.asn_recorded_at) : '—'}
                {pl.asn_email
                  ? <> · <a href={asnFileHref(pl.asn_email)} target="_blank" rel="noreferrer">📎 approval mail</a></>
                  : <> · <span style={{ color: '#B45309' }}>no approval mail attached</span></>}
              </div>
            </>
          ) : (
            <div style={{ fontSize: 13, color: '#8A6D00' }}>
              No ASN recorded. The buyer must confirm before these goods can leave — a dispatch will be refused without it.
            </div>
          )}
        </div>
        <div style={{ flex: '0 0 auto' }}>
          {plLocked
            ? <span style={{ fontSize: 11, color: '#888' }}>🔒 dispatched — reference and date locked</span>
            : <button onClick={() => setAsnOpen(true)} style={{ ...btn, background: hasAsn(pl) ? '#f0f2f8' : '#B45309', color: hasAsn(pl) ? '#555' : '#fff' }}>{hasAsn(pl) ? 'Edit ASN' : 'Record ASN'}</button>}
        </div>
      </div>
      {recon && (
        <div style={{ border: '1px solid #eee', borderRadius: 8, padding: 12, marginBottom: 14 }}>
          <b style={{ fontSize: 13 }}>Pack-vs-Cut ({pl.gpo_no})</b>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 6 }}>
            <thead><tr>{['Size', 'Cut', 'Packed', 'Variance'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>{recon.rows.map(r => <tr key={r.size_label} style={{ background: r.over_packed ? '#FEF2F2' : '#fff' }}>
              <td style={td}>{r.size_label}</td><td style={{ ...td, textAlign: 'right' }}>{num(r.cut_qty)}</td><td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{num(r.packed_qty)}</td>
              <td style={{ ...td, textAlign: 'right', color: r.over_packed ? '#B91C1C' : '#888' }}>{r.variance > 0 ? '+' : ''}{num(r.variance)}{r.over_packed ? ' ⚠' : ''}</td></tr>)}</tbody>
          </table>
          <div style={{ fontSize: 11, color: '#B45309', marginTop: 6 }}>Sewn total: {recon.sewn_total ?? '—'} · {recon.sewn_note}</div>
        </div>
      )}
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr>{['Carton', 'Type', 'Colour', 'Sizes', 'Gross/Net', 'Dispatched?', ''].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
        <tbody>
          {(pl.cartons || []).length === 0 && <tr><td colSpan={7} style={{ ...td, color: '#aaa' }}>No cartons yet.</td></tr>}
          {(pl.cartons || []).map(c => (
            <tr key={c.id}>
              <td style={{ ...td, fontWeight: 700 }}>#{c.carton_no}</td><td style={{ ...td, fontSize: 11 }}>{c.carton_type}{c.ratio_desc ? ` (${c.ratio_desc})` : ''}</td>
              <td style={td}>{c.colour || '—'}</td>
              <td style={td}>{(c.lines || []).map(l => `${l.size_label}×${l.qty}`).join('  ')}</td>
              <td style={{ ...td, fontSize: 11 }}>{c.gross_wt_kg != null ? num(c.gross_wt_kg) : '—'}/{c.net_wt_kg != null ? num(c.net_wt_kg) : '—'}</td>
              <td style={td}>{c.dispatched_on ? <span style={{ color: '#3B3A8F', fontSize: 11, fontWeight: 600 }}>shipped</span> : <span style={{ color: '#166534', fontSize: 11 }}>in FG</span>}</td>
              <td style={td}>{pl.status === 'draft' && !c.dispatched_on && <button onClick={() => delCarton(c.id)} style={{ border: 'none', background: 'none', color: '#B91C1C', cursor: 'pointer' }}>✕</button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {adding && <AddCarton pl={pl} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); reload(); }} />}
      {asnOpen && <AsnModal pl={pl} onClose={() => setAsnOpen(false)} onSaved={() => { setAsnOpen(false); reload(); }} />}
    </div>
  );
}

function AddCarton({ pl, onClose, onSaved }) {
  const [type, setType] = React.useState('solid'); const [colour, setColour] = React.useState(''); const [ratioDesc, setRatioDesc] = React.useState('');
  const [gross, setGross] = React.useState(''); const [net, setNet] = React.useState('');
  const [lines, setLines] = React.useState([{ size_label: '', qty: '' }]);
  const [err, setErr] = React.useState(''); const [busy, setBusy] = React.useState(false);
  const setLine = (i, p) => setLines(ls => ls.map((l, j) => j === i ? { ...l, ...p } : l));
  const save = async () => {
    setErr('');
    const valid = lines.filter(l => l.size_label && num(l.qty) > 0);
    if (!valid.length) return setErr('At least one size line.');
    if (type === 'solid' && valid.length !== 1) return setErr('A solid carton must have exactly one size line.');
    setBusy(true);
    try {
      await api.post(`/dispatch/packing-lists/${pl.id}/cartons`, { carton_type: type, colour, ratio_desc: ratioDesc || null,
        gross_wt_kg: gross ? num(gross) : null, net_wt_kg: net ? num(net) : null, lines: valid.map(l => ({ size_label: l.size_label, qty: num(l.qty) })) });
      onSaved();
    } catch (e) { setErr(e.response?.data?.error || 'Failed'); } finally { setBusy(false); }
  };
  return <Modal title={`Add Carton to ${pl.pl_no}`} onClose={onClose}>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
      <label style={{ fontSize: 11, color: '#555' }}>Type<br /><select value={type} onChange={e => setType(e.target.value)} style={{ ...inp, width: '100%' }}><option value="solid">Solid (1 size)</option><option value="ratio">Ratio (mixed sizes)</option></select></label>
      <label style={{ fontSize: 11, color: '#555' }}>Colour<br /><input value={colour} onChange={e => setColour(e.target.value)} style={{ ...inp, width: '100%' }} /></label>
      <label style={{ fontSize: 11, color: '#555' }}>Gross wt (kg, opt)<br /><input type="number" value={gross} onChange={e => setGross(e.target.value)} style={{ ...inp, width: '100%' }} /></label>
      <label style={{ fontSize: 11, color: '#555' }}>Net wt (kg, opt)<br /><input type="number" value={net} onChange={e => setNet(e.target.value)} style={{ ...inp, width: '100%' }} /></label>
    </div>
    {type === 'ratio' && <label style={{ fontSize: 11, color: '#555' }}>Ratio note (print only, e.g. "1-2-2-1 × 6")<br /><input value={ratioDesc} onChange={e => setRatioDesc(e.target.value)} style={{ ...inp, width: '100%', margin: '3px 0 10px' }} /></label>}
    <div style={{ fontSize: 12, fontWeight: 700, color: '#555', margin: '6px 0 4px' }}>Size lines (absolute pieces)</div>
    {lines.map((l, i) => (
      <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
        <input placeholder="size" value={l.size_label} onChange={e => setLine(i, { size_label: e.target.value })} style={{ ...inp, flex: 1 }} />
        <input type="number" placeholder="qty" value={l.qty} onChange={e => setLine(i, { qty: e.target.value })} style={{ ...inp, width: 90 }} />
        {lines.length > 1 && <button onClick={() => setLines(ls => ls.filter((_, j) => j !== i))} style={{ border: 'none', background: 'none', color: '#B91C1C', cursor: 'pointer' }}>✕</button>}
      </div>
    ))}
    {type === 'ratio' && <button onClick={() => setLines(ls => [...ls, { size_label: '', qty: '' }])} style={{ ...btn, background: '#eef', color: '#3B3A8F', marginBottom: 10 }}>+ size</button>}
    {err && <div style={{ color: '#B91C1C', fontSize: 13, margin: '8px 0' }}>{err}</div>}
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}><button onClick={onClose} style={{ ...btn, background: '#f0f2f8', color: '#555' }}>Cancel</button><button onClick={save} disabled={busy} style={btn}>Add carton</button></div>
  </Modal>;
}

// Record the buyer's ASN against a packing list. THREE INPUTS AND NO MORE.
//   · the reference is the BUYER'S string from the BUYER'S system — never generated here;
//   · the date is the buyer's approval date, TYPED, because the proof is a PDF, a forwarded
//     .eml or a phone photo and a parser that succeeds on two formats and guesses on the
//     third is worse than an honest text field;
//   · the mail is OPTIONAL BY RULING — a hard block would be met with a blank upload inside a
//     week, so the gap is counted at dispatch (asn_email_missing_count) instead of forced.
// There is no quantity field: an ASN's quantity is always exactly this packing list.
function AsnModal({ pl, onClose, onSaved }) {
  const [ref, setRef] = React.useState(pl.asn_ref || '');
  const [appr, setAppr] = React.useState(pl.asn_approved_at ? String(pl.asn_approved_at).slice(0, 10) : '');
  const [file, setFile] = React.useState(null);
  const [err, setErr] = React.useState(''); const [busy, setBusy] = React.useState(false);
  // ONE BOUND, AND IT IS THE FUTURE ONE. It mirrors the server's own check so the common typo
  // costs no round trip; it is a convenience, not the authority — the server re-checks against
  // ITS date and returns the message we show, because a client clock is not evidence.
  //
  // THERE IS DELIBERATELY NO LOWER BOUND. This field held min={pl.created_at}, which refused any
  // approval dated before the packing-list ROW was keyed — and that is not when the packing list
  // existed, it is when somebody typed it in. The whole reason asn_approved_at is separate from
  // asn_recorded_at is that the buyer approves before we write it down; bounding the first by our
  // entry of the second collapsed the distinction the two fields exist to keep. A back date is
  // the normal case here, not the suspicious one.
  const today = new Date().toISOString().slice(0, 10);
  const save = async () => {
    setErr('');
    if (!ref.trim()) return setErr('The ASN reference is required — it is the buyer’s, from their system.');
    if (!appr) return setErr('A reference with no approval date is not an approval.');
    setBusy(true);
    try {
      // multipart: the route takes the attachment on the same PATCH, so a JSON body would
      // silently drop the file.
      const fd = new FormData();
      fd.append('asn_ref', ref.trim());
      fd.append('asn_approved_at', appr);
      if (file) fd.append('asn_email', file);
      await api.patch(`/dispatch/packing-lists/${pl.id}/asn`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onSaved();
    } catch (e) { setErr(e.response?.data?.error || 'Failed'); setBusy(false); }
  };
  return <Modal title={`Buyer\u2019s ASN \u2014 ${pl.pl_no}`} onClose={onClose}>
    <div style={{ fontSize: 12, color: '#666', marginBottom: 12 }}>
      Raised and approved in the buyer’s system, recorded here. The quantity is always exactly this packing list, so there is nothing to enter.
    </div>
    <label style={{ fontSize: 12, color: '#555' }}>ASN reference (the buyer’s) *<br />
      <input value={ref} onChange={e => setRef(e.target.value)} maxLength={60} style={{ ...inp, width: '100%', margin: '3px 0 10px' }} placeholder="as it appears in their system" /></label>
    <label style={{ fontSize: 12, color: '#555' }}>Buyer approved on *<br />
      <input type="date" value={appr} max={today} onChange={e => setAppr(e.target.value)} style={{ ...inp, width: '100%', margin: '3px 0 4px' }} /></label>
    <div style={{ fontSize: 11, color: '#888', marginBottom: 10 }}>The date on the buyer’s approval — not the date you are entering it.</div>
    <label style={{ fontSize: 12, color: '#555' }}>Approval mail (optional)<br />
      <input type="file" accept=".pdf,.png,.jpg,.jpeg,.eml,.msg,.docx" onChange={e => setFile(e.target.files?.[0] || null)} style={{ ...inp, width: '100%', margin: '3px 0 4px' }} /></label>
    <div style={{ fontSize: 11, color: '#888', marginBottom: 10 }}>PDF, image, .eml, .msg or .docx, up to 10&nbsp;MB. Not required to dispatch, but a missing one is counted.</div>
    {err && <div style={{ color: '#B91C1C', fontSize: 13, marginBottom: 8 }}>{err}</div>}
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
      <button onClick={onClose} style={{ ...btn, background: '#f0f2f8', color: '#555' }}>Cancel</button>
      <button onClick={save} disabled={busy} style={btn}>{busy ? '\u2026' : 'Save ASN'}</button>
    </div>
  </Modal>;
}

function Dispatches({ user }) {
  const [rows, setRows] = React.useState([]); const [creating, setCreating] = React.useState(false);
  // SERVER-SIDE: GET /dispatch/dispatches is capped at LIMIT 500. A dispatch has no GPO of
  // its own — the server matches through its packing lists.
  const [gpoQ, setGpoQ] = React.useState('');
  const load = React.useCallback(() => api.get('/dispatch/dispatches' + (gpoQ.trim() ? '?gpo_no=' + encodeURIComponent(gpoQ.trim()) : ''))
    .then(r => setRows(r.data.data || [])).catch(() => {}), [gpoQ]);
  React.useEffect(() => { load(); }, [load]);
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <GpoFilter value={gpoQ} onChange={setGpoQ} />
          <span style={{ fontSize: 12, color: '#888' }}>A dispatch ships selected cartons against a mandatory non-returnable gate pass (= the approval).</span>
        </div>
        <button onClick={() => setCreating(true)} style={btn}>+ New Dispatch</button>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr>{['DC No', 'Buyer', 'Delivery Centre', 'Packing Lists', 'Gate Pass', 'Cartons', 'Date', 'Status'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={8} style={{ ...td, textAlign: 'center', color: '#aaa' }}>{gpoQ.trim() ? `No dispatch matches GPO “${gpoQ.trim()}”.` : 'No dispatches yet.'}</td></tr>}
          {rows.map(r => (
            <tr key={r.id}>
              <td style={{ ...td, fontFamily: 'monospace', fontWeight: 700, color: '#3B3A8F' }}>{r.dc_no}</td>
              <td style={td}>{r.buyer_name || '—'}</td>
              <td style={td}>{r.dc_name || '—'}</td>
              <td style={{ ...td, fontSize: 11 }}>{(r.packing_lists || []).map(p => `${p.pl_no} (${p.gpo_no})`).join(', ') || '—'}</td>
              <td style={{ ...td, fontFamily: 'monospace', fontSize: 11 }}>{r.gp_no || '—'}</td>
              <td style={{ ...td, textAlign: 'center' }}>{r.carton_count}</td>
              <td style={{ ...td, fontSize: 11 }}>{(r.dispatch_date || '').slice(0, 10)}</td><td style={td}>{pill(r.status)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {creating && <NewDispatch onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
    </div>
  );
}

function NewDispatch({ onClose, onSaved }) {
  const [pls, setPls] = React.useState([]);
  const [plIds, setPlIds] = React.useState([]);          // selected PLs (multi)
  const [details, setDetails] = React.useState({});      // plId -> detail (with cartons)
  const [cartonIds, setCartonIds] = React.useState([]);
  const [gps, setGps] = React.useState([]); const [gpId, setGpId] = React.useState('');
  const [transporters, setTransporters] = React.useState([]);
  const [dcs, setDcs] = React.useState([]); const [dcId, setDcId] = React.useState('');
  const [form, setForm] = React.useState({ transporter_id: '', vehicle_no: '', lr_no: '', destination: '' });
  const [err, setErr] = React.useState(''); const [busy, setBusy] = React.useState(false); const [warn, setWarn] = React.useState(null);
  React.useEffect(() => {
    api.get('/dispatch/packing-lists').then(r => setPls((r.data.data || []).filter(p => p.status === 'completed'))).catch(() => {});
    api.get('/dispatch/eligible-gate-passes').then(r => setGps(r.data.data || [])).catch(() => {});
    api.get('/dispatch/transporters').then(r => setTransporters(r.data.data || [])).catch(() => {});
  }, []);

  // The buyer is derived from the selected PLs; all must be the same buyer (backend also enforces).
  const selectedPls = pls.filter(p => plIds.includes(p.id));
  const buyerIds = [...new Set(selectedPls.map(p => p.buyer_id).filter(Boolean))];
  const buyerId = buyerIds.length === 1 ? buyerIds[0] : null;
  const buyerName = selectedPls.find(p => p.buyer_id === buyerId)?.buyer_name || '';
  const mixedBuyer = buyerIds.length > 1;
  // Completed, but the buyer has not confirmed them - the mig-233 gate will refuse these.
  const blockedPls = pls.filter(p => !hasAsn(p));

  // Load DCs for the resolved buyer.
  React.useEffect(() => {
    if (!buyerId) { setDcs([]); setDcId(''); return; }
    api.get('/logistics/delivery-centres?buyer_id=' + buyerId).then(r => setDcs(r.data.data || [])).catch(() => setDcs([]));
    setDcId('');
  }, [buyerId]);

  const togglePL = (id) => {
    setPlIds(a => {
      const next = a.includes(id) ? a.filter(x => x !== id) : [...a, id];
      if (!a.includes(id) && !details[id]) api.get('/dispatch/packing-lists/' + id).then(r => setDetails(d => ({ ...d, [id]: r.data.data }))).catch(() => {});
      // dropping a PL drops its cartons from the selection
      if (a.includes(id)) {
        const drop = new Set((details[id]?.cartons || []).map(c => c.id));
        setCartonIds(cs => cs.filter(cid => !drop.has(cid)));
      }
      return next;
    });
  };
  const toggleCarton = (cid) => setCartonIds(a => a.includes(cid) ? a.filter(x => x !== cid) : [...a, cid]);

  const save = async () => {
    setErr('');
    if (!plIds.length) return setErr('Select at least one packing list.');
    if (mixedBuyer) return setErr('All packing lists must be the same buyer — one buyer per truck.');
    if (!gpId) return setErr('Pick a gate pass (the approval).');
    if (!cartonIds.length) return setErr('Select at least one carton.');
    setBusy(true);
    try {
      const r = await api.post('/dispatch/dispatches', {
        packing_list_ids: plIds, gate_pass_id: gpId, carton_ids: cartonIds,
        buyer_dc_id: dcId || null, ...form,
      });
      if (r.data.data.warnings?.length) { setWarn(r.data.data.warnings); setTimeout(onSaved, 3000); } else onSaved();
    } catch (e) { setErr(e.response?.data?.error || 'Failed'); } finally { setBusy(false); }
  };

  // Cartons available across the SELECTED PLs only (constraint: only cartons on chosen PLs).
  const openCartonsByPl = plIds.map(pid => ({ pid, pl: pls.find(p => p.id === pid), cartons: (details[pid]?.cartons || []).filter(c => !c.dispatched_on) }));

  return <Modal title="New Dispatch (Consignment)" onClose={onClose} wide>
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11, color: '#555', fontWeight: 700, marginBottom: 4 }}>Completed packing lists (one buyer per truck, each confirmed by the buyer) *</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 120, overflowY: 'auto' }}>
        {pls.length === 0 && <span style={{ fontSize: 12, color: '#aaa' }}>No completed packing lists.</span>}
        {/* WHY THE STATE IS ON THE ROW AND NOT ONLY IN THE REFUSAL. These were bare checkboxes:
            an operator picked lists, filled in transport, submitted, and only THEN met the
            collected "no approved ASN" message. The gate below is still the authority and its
            wording is unchanged - this just stops the refusal being the first place anyone
            learns that half the pile cannot travel. */}
        {pls.map(p => {
          const ok = hasAsn(p);
          return (
            <label key={p.id}
              title={ok ? `ASN ${p.asn_ref} \u00b7 approved ${dstr(p.asn_approved_at)}` : 'The buyer has not confirmed this packing list \u2014 record its ASN on the packing list first.'}
              style={{ fontSize: 12, border: '1px solid ' + (ok ? '#ddd' : '#F3D7A8'), borderRadius: 6, padding: '4px 8px', cursor: ok ? 'pointer' : 'not-allowed', background: !ok ? '#FFFBF2' : (plIds.includes(p.id) ? '#EEF' : '#fff'), color: ok ? 'inherit' : '#8A6D00' }}>
              <input type="checkbox" disabled={!ok} checked={plIds.includes(p.id)} onChange={() => togglePL(p.id)} /> {p.pl_no} · {p.gpo_no} <span style={{ color: '#999' }}>({p.buyer_name || 'no buyer'})</span> {asnChip(p)}
            </label>
          );
        })}
      </div>
      {blockedPls.length > 0 && (
        <div style={{ fontSize: 12, color: '#8A6D00', marginTop: 5 }}>
          {blockedPls.length === 1 ? '1 packing list is' : `${blockedPls.length} packing lists are`} waiting on the buyer’s ASN and cannot travel: {blockedPls.map(p => `${p.pl_no} (GPO ${p.gpo_no})`).join(', ')}. Record it on the packing list.
        </div>
      )}
      {buyerId && <div style={{ fontSize: 12, color: '#0F7B3F', marginTop: 4 }}>Buyer: <b>{buyerName}</b></div>}
      {mixedBuyer && <div style={{ fontSize: 12, color: '#B91C1C', marginTop: 4 }}>⚠ Mixed buyers selected — one buyer per truck. Deselect the others.</div>}
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
      <label style={{ fontSize: 11, color: '#555' }}>Delivery centre<br />
        <select value={dcId} onChange={e => setDcId(e.target.value)} disabled={!buyerId} style={{ ...inp, width: '100%' }}>
          <option value="">— select DC —</option>{dcs.map(dc => <option key={dc.id} value={dc.id}>{dc.dc_name}{dc.city ? ' · ' + dc.city : ''}</option>)}
        </select></label>
      <label style={{ fontSize: 11, color: '#555' }}>Gate pass (approved/exited, non-returnable) *<br />
        <select value={gpId} onChange={e => setGpId(e.target.value)} style={{ ...inp, width: '100%' }}><option value="">— select —</option>{gps.map(g => <option key={g.id} value={g.id}>{g.gp_no} ({g.status})</option>)}</select></label>
    </div>
    {openCartonsByPl.length > 0 && (
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 4 }}>Cartons to ship ({cartonIds.length} selected) — only cartons on the selected packing lists</div>
        {openCartonsByPl.map(({ pid, pl, cartons }) => (
          <div key={pid} style={{ marginBottom: 6 }}>
            <div style={{ fontSize: 11, color: '#888' }}>{pl?.pl_no} · {pl?.gpo_no}</div>
            {cartons.length === 0 ? <div style={{ fontSize: 12, color: '#aaa' }}>All cartons on this PL are already dispatched.</div> :
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {cartons.map(c => <label key={c.id} style={{ fontSize: 12, border: '1px solid #ddd', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', background: cartonIds.includes(c.id) ? '#EEF' : '#fff' }}>
                  <input type="checkbox" checked={cartonIds.includes(c.id)} onChange={() => toggleCarton(c.id)} /> #{c.carton_no} ({(c.lines || []).map(l => `${l.size_label}×${l.qty}`).join(',')})</label>)}
              </div>}
          </div>
        ))}
      </div>
    )}
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
      <label style={{ fontSize: 11, color: '#555' }}>Transporter<br />
        <select value={form.transporter_id} onChange={e => setForm(f => ({ ...f, transporter_id: e.target.value, transporter: transporters.find(t => t.id === e.target.value)?.name || '' }))} style={{ ...inp, width: '100%' }}>
          <option value="">— select —</option>{transporters.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}</select></label>
      <label style={{ fontSize: 11, color: '#555' }}>Vehicle no<br /><input value={form.vehicle_no} onChange={e => setForm(f => ({ ...f, vehicle_no: e.target.value }))} style={{ ...inp, width: '100%' }} /></label>
      <label style={{ fontSize: 11, color: '#555' }}>LR / AWB<br /><input value={form.lr_no} onChange={e => setForm(f => ({ ...f, lr_no: e.target.value }))} style={{ ...inp, width: '100%' }} /></label>
      <label style={{ fontSize: 11, color: '#555' }}>Destination (defaults to DC)<br /><input value={form.destination} onChange={e => setForm(f => ({ ...f, destination: e.target.value }))} style={{ ...inp, width: '100%' }} /></label>
    </div>
    {warn && <div style={{ background: '#FFF4E5', border: '1px solid #F0D98C', borderRadius: 6, padding: 8, fontSize: 12, color: '#8A6D00', marginBottom: 8 }}>⚠ {warn.join(' ')}</div>}
    {err && <div style={{ color: '#B91C1C', fontSize: 13, marginBottom: 8 }}>{err}</div>}
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}><button onClick={onClose} style={{ ...btn, background: '#f0f2f8', color: '#555' }}>Cancel</button><button onClick={save} disabled={busy || mixedBuyer} style={btn}>Dispatch</button></div>
  </Modal>;
}

function FGView() {
  const [rows, setRows] = React.useState([]); const [gpo, setGpo] = React.useState('');
  const load = React.useCallback(() => api.get('/dispatch/fg' + (gpo.trim() ? '?gpo_no=' + encodeURIComponent(gpo.trim()) : '')).then(r => setRows(r.data.data || [])).catch(() => {}), [gpo]);
  React.useEffect(() => { load(); }, [load]);
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center' }}>
        <GpoFilter value={gpo} onChange={setGpo} width={200} />
        <span style={{ fontSize: 12, color: '#888' }}>FG on hand = packed − dispatched, per GPO/style/size (a view over documents — cancel a DC and cartons return here).</span>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr>{['GPO', 'Style', 'Size', 'Packed', 'Dispatched', 'FG on hand'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={6} style={{ ...td, textAlign: 'center', color: '#aaa' }}>{gpo.trim() ? `Nothing packed for GPO “${gpo.trim()}”.` : 'Nothing packed yet.'}</td></tr>}
          {rows.map((r, i) => (
            <tr key={i}>
              <td style={td}>{r.gpo_no}</td><td style={td}>{r.style_no || '—'}</td><td style={{ ...td, fontWeight: 600 }}>{r.size_label}</td>
              <td style={{ ...td, textAlign: 'right' }}>{num(r.packed_qty)}</td><td style={{ ...td, textAlign: 'right' }}>{num(r.dispatched_qty)}</td>
              <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: num(r.fg_on_hand) > 0 ? '#166534' : '#888' }}>{num(r.fg_on_hand)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
